import React, { useState, useRef, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import { Trash2 } from "lucide-react";
import axios from "axios";
import Modal from "../../common/CommonModal";
import ProductListing from "../../salesInvoice/ProductsModal";
import useApi from "../../../hooks/useApi";
import canUserSeeThis from "../../common/CheckCanUserSeeThis";

const PartTable = ({ mode, title, mainCode, isEdit, onEntriesChange }) => {
  const [entries, setEntries] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [activeRowIndex, setActiveRowIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("UNIVERSAL");
  const COMPANY_CODE = localStorage.getItem("companyCode");
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG;
  const [products, setProducts] = useState([]);
  const { request } = useApi();

  useEffect(() => {
    if (mainCode.length > 0) {
      fetchPartTableData();
    }
  }, [isEdit]);

  const fetchPartTableData = async () => {
    try {
      let company_code_from_local_storage = localStorage.getItem("companyCode")
        ? localStorage.getItem("companyCode")
        : "-";

      let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";

      const apiBaseURL = apiPaths_from_local_storage[0]?.API_CONFIG;

      const response = await axios.get(
        `${apiBaseURL}/api/CpaysCount/${company_code_from_local_storage}/${mode}/${mainCode}/-`
      );
      const data = response.data;

      const mappedData = data.map((item) => ({
        oemBrand: item.code || "Unknown",
        partNumber: item.c_description || "",
        quantity: item.Qty || 0,
        selected: false,
      }));

      setEntries(mappedData);
    } catch (error) {
      console.error("Error While fetching Genuine Equivalents:", error);
    }
  };

  const toggleSelectAll = (checked) => {
    setSelectAll(checked);
    setEntries(entries.map((row) => ({ ...row, selected: checked })));
  };

  const toggleRow = (idx, checked) => {
    const newEntries = [...entries];
    newEntries[idx].selected = checked;
    setEntries(newEntries);

    const allSelected = newEntries.every((r) => r.selected);
    const someSelected = newEntries.some((r) => r.selected);
    setSelectAll(allSelected && someSelected);
  };

  const updateCell = (idx, field, value) => {
    const newEntries = [...entries];
    newEntries[idx][field] = value;
    setEntries(newEntries);
  };

  const addRow = () => {
    setEntries([
      ...entries,
      { oemBrand: "", partNumber: "", quantity: "", selected: false },
    ]);
  };

 

  const deleteSelected = () => {
    setEntries(entries.filter((r) => !r.selected));
    setSelectAll(false);
  };

  const fileInputRef = useRef(null);

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });

      const imported = rows.slice(1).map((r) => ({
        oemBrand: r[0] ?? "",
        partNumber: r[1] ?? "",
        quantity: r[2] ?? "",
        selected: false,
      }));

      setEntries([...entries, ...imported]);
    };
    reader.readAsBinaryString(file);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fetchProductData = useCallback(
    async (query, searchByValue, location = "A") => {
      // Convert empty string to "-"
      const effectiveQuery = query?.trim() === "" ? "-" : query?.trim() || "-";
      const formattedQuery = effectiveQuery.replace(/\//g, "%2F");

      try {
        setLoading(true);
        const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
        const response = await request({
          url: url,
          method: "GET",
          timeout: 5000,
        });
        if (response) {
          setProducts(response);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Failed to fetch product data", err);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [BASE_URL, COMPANY_CODE, request]
  );
  useEffect(() => {
    fetchProductData(searchQuery, searchBy);
  }, [searchQuery, searchBy]);

  const handleProductSelect = (product) => {
    console.log("Selected Product:", product);
    if (activeRowIndex === null) return;

    setEntries((prev) => {
      const updated = [...prev];
      updated[activeRowIndex] = {
        ...updated[activeRowIndex],
        oemBrand: product.Sgroup || "",
        partNumber: product.Code || "",
        unit: product.Unit || "",
        // Qty: product.QTY || updated[activeRowIndex].Qty || "",
      };
      return updated;
    });

    setCodeModalOpen(false);
    setActiveRowIndex(null);
  };


  useEffect(() => {
    onEntriesChange(mode, entries);
  }, [entries]);

  return (
    <div className="mt-6 ">
      <div className="h-[400px] bg-white  overflow-auto border rounded-md shadow-sm">
        <table className="text-sm bg-white  w-full ">
          <thead className="bg-gray-100 sticky top-0 z-20">
            <tr className="h-10">
              <th className="border px-2 py-1 text-center ">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-400 cursor-pointer"
                />
              </th>

              <th className="border px-2 py-1 font-normal">{title}</th>
              <th className="border px-2 py-1 font-normal">Part No</th>
              <th className="border px-2 py-1 w-6 font-normal">Qty</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white border border-gray-300">
            {entries.length === 0 ? (
              <tr className="h-[34vh]">
                <td colSpan={8} className=" text-center py-8 text-gray-500">
                  No parts added yet.
                </td>
              </tr>
            ) : (
              entries.map((row, idx) => (
                <tr key={idx} className="border border-gray-300">
                  {/* Checkbox */}
                  <td className="border-x px-2 text-center">
                    <input
                      type="checkbox"
                      checked={row.selected ?? false}
                      onChange={(e) => toggleRow(idx, e.target.checked)}
                      className="w-4 h-4 rounded border-gray-400 cursor-pointer"
                    />
                  </td>

                  {/* OEM Brand */}
                  <td className="border-x p-1">
                    <input
                      // type="text"
                      onKeyDown={(e) => {
                        if (e.key === "F2") {
                          setActiveRowIndex(idx);
                          setCodeModalOpen(true);
                        }
                      }}
                      placeholder="Enter or F2 press to select Item"
                      value={row.oemBrand ?? ""}
                      onChange={(e) =>
                        updateCell(idx, "oemBrand", e.target.value)
                      }

                      className="w-full px-2 text-xs border-none focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                    />
                  </td>

                  {/* Part Number */}
                  <td className="border-x p-1">
                    <input
                      // type="text"
                      value={row.partNumber ?? ""}
                      onChange={(e) =>
                        updateCell(idx, "partNumber", e.target.value)
                      }
                      placeholder="Part No"
                      className="w-full  text-xs border-none focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                    />
                  </td>

                  {/* Quantity */}
                  <td className="border-x bg-gray-100 ">
                    <input
                      readOnly
                      type="number"
                      value={row.quantity || "0"}
                      // onChange={(e) =>
                      //   updateCell(idx, "quantity", e.target.value)
                      // }
                      // placeholder="0"
                      className="bg-gray-100 cursor-not-allowed w-full   text-xs border-none text-right focus:outline-none rounded"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canUserSeeThis("AE") &&
        <div className="flex justify-between items-center mt-4">
          <button
            onClick={addRow}
            className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            + Add
          </button>

          {/* <button
          onClick={()=>savePartTable(mode,entries)}
          className="px-2 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          Save
        </button> */}

          {entries.some((r) => r.selected) && (
            <button
              onClick={deleteSelected}
              className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center"
            >
              <Trash2 className="w-3 h-4 mr-2" />
              Delete
            </button>
          )}

          <label className="cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImport}
              className="hidden"
            />
            <span className="px-2 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 inline-block">
              Import
            </span>
          </label>
        </div>
      }

      {codeModalOpen && (
        <Modal
          isOpen={codeModalOpen}
          onClose={() => setCodeModalOpen(false)}
          title={"Select Item Code"}
          size="xxl"
          header={true}
        >
          <ProductListing
            modalSuggestions={products}
            loading={loading}
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onSearchQueryChange={setSearchQuery}
            handleProductSelect={handleProductSelect}
          />
        </Modal>
      )}
    </div>
  );
};

export default PartTable;
